import { PropsWithChildren, useState, useCallback } from 'react';

import { getAuthorization } from '@utils';
import { TenantThemeProvider } from '@packlink/tenant-theme-provider';
import { useTenantConfig } from '@packlink/tenant-config-provider';
import { useTranslation } from '@packlink/translation-provider';

export const loadTheme = async (brand: string): Promise<void> =>
    import(`../assets/styles/brands/${brand.toLowerCase()}.scss`)
        .catch(() => import('../assets/styles/brands/ebay.scss'))
        .then((styles) => {
            if (styles === undefined) {
                throw new Error(`Problem loading brand "${brand.toLowerCase()}" maybe the path is incorrect!`);
            }
        });

function useSassTheme() {
    const [_, setErrorState] = useState<string | null>(null);
    const { brand } = useTenantConfig<ITenantConfig>();

    const onLoadBrand = useCallback(() => {
        loadTheme(brand).catch((error) => {
            const token = getAuthorization();

            // Fixes Sentry loggin too many events in Firefox due to not being able to finish
            // the download of some files because of the logout redirect.
            // https://auctane.atlassian.net/browse/PCK-950
            if (!token) return;

            // React not capturing inside error boundary the errors produced in async request
            // https://github.com/facebook/react/issues/14981#issuecomment-468460187
            setErrorState(() => {
                throw new Error(error);
            });
        });
    }, [brand]);
    return onLoadBrand;
}

export const BrandProvider = ({ children }: PropsWithChildren<unknown>): JSX.Element => {
    const { t } = useTranslation();
    const onLoad = useSassTheme();

    return (
        <TenantThemeProvider
            onLoad={onLoad}
            headerProps={{ title: t('html.head.title'), description: t('html.meta.description') }}
        >
            {children}
        </TenantThemeProvider>
    );
};
